import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const TIMER_DURATION = 45; // seconds

const Timer = ({ gameId, onRefresh }) => {
  const [timeLeft, setTimeLeft] = useState(TIMER_DURATION);
  const [isRunning, setIsRunning] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    // Create audio element for timer end sound
    audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSx+zPLTgjMGHmnB6+OdTxALUKnn77RgGwU7k9n0zn0vBSh+zPPUhzcIHGq+7+WhUBEMUKjo8bViFgU6kdnyz30vBSd9y/PWhzsKHGu/8OSgTxELT6fn8LRgHAU5kdj0zoI0Byh9y/PVhzkKHWq+7+OgUREKT6jo8LVhHQU5kdj0z4I1Byh9y/PVhzoKHWq+7+KgURIMT6jo8LRhHQU5kNf0z4M1CCh9y/PUhjoKHGq+7+KfURIMTqfn8LNhHgU5kNf0z4M2CCh9y/PUhjsKHGm+7+KfUhIMTqfn8LNhHgU5kNf0z4M2CCh9y/PUhzsKG2m+7+KfUhIMTqfn8LJhHwU5kNf0z4M3CCd8y/PUhzsKG2m97+KfUhIMTqfn8LJhHwU5j9f0z4M3CCd8y/PUhzsKG2m97+GfUxMMTqfn8LJhHwU5j9f0z4M3CCd8y/PThzsKG2m97+GfUxMMTqfm8LJhHwU5j9f0zoM3CCd7y/PThzsKGmi97+GfUxQMTqfl8LFhHwU5jtf0zoM3CCd7y/PThzsKGmi97+CfUxQMTafm8LFhIAU5jtf0zoM4CCd7y/PThzsKGmi97+CfUxQMTafm8LFhIAU5jtf0zoM4CCd7y/PThjoKGmi87+CfUxQMTafm8LFhIAU5jtb0zoM4CCd7y/PThjoKGmi87+CeUxUMTafm8LBgIAU5jtb0zoM4CCd7y/PThjoKGme87+CeUxUMTafm8LBgIAU5jtb0zoM4CCd7y/PShToKGme87+CeUxUMTafm8LBgIAU5jtb0zoM4CCd7y/PShToKGme87+CeUxUMTafm8LBgIAU5jdb0zoM4CCd7y/PShToKGme87+CeUxUMTafm8LBgIAU5jdb0zoM4CCd7y/PShToKGme87+CeUxUMTafm8LBgIAU5jdb0zoM4CCd7y/PShToKGme87+CeUxUMTafm8LBgIAU5jdb0zoM4CCd7yvLShToJGWe87+CeVBYMTafm77BgIQU5jdb0zYI4CCZ7yvLShToJGWe87+CeVBYMTafm77BgIQU5jdb0zYI4CCZ7yvLShToJGWe87+CeVBYMTKbm77BgIwU5jdX0zYI4CCV7yvLRhToJGWa87+CeVBYMTKbm77BgIwU5jNX0zYI4CCV7yvLRhToJGWa87+CeVBYMTKbm77BgIwU5jNX0zYI4CCV7yvLRhToJGWa87+CeVBYMTKbm77BgIwU5jNX0zYI4CCV7yvLRhToJGWa87+CeVBYMTKbm77BgIwU5jNX0zYI4CCV7yvLRhToJGWa87+CeVBYMTKbm77BgIwU5jNX0zYI4CCV7yvLRhToJGWa87+CeVBYMTKbm77BgIwU5jNX0zYI4CCV7yvLRhToJGWa87+CeVBYMTKbm77BgIwU5jNX0zYI4CCV7yvLRhToJGWa87+CeVBYMTKbm77BgIwU5jNX0zYI4CCV7yvLRhToJGWa87+CeVBYMTKbm77BgIwU5jNX0zYI4CCV7yvLRhToJGWa87+CeVBYMTKbm77BgIwU5jNX0zYI4CCV7yvLRhToJGWa87+CeVBYM');
  }, []);

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setIsRunning(false);
            setHasEnded(true);
            if (audioRef.current) {
              audioRef.current.play().catch(e => console.log('Audio play failed:', e));
            }
            toast.warning('Time\'s up!', {
              duration: 3000,
            });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, timeLeft]);

  const handleStart = () => {
    if (timeLeft === 0) {
      setTimeLeft(TIMER_DURATION);
      setHasEnded(false);
    }
    setIsRunning(true);
  };

  const handlePause = () => {
    setIsRunning(false);
  };

  const handleReset = () => {
    setIsRunning(false);
    setTimeLeft(TIMER_DURATION);
    setHasEnded(false);
  };

  const progress = (timeLeft / TIMER_DURATION) * 100;
  const isWarning = timeLeft <= 10 && timeLeft > 0;
  const isDanger = timeLeft <= 5 && timeLeft > 0;

  return (
    <Card className="overflow-hidden" data-testid="timer-card">
      <CardHeader className="bg-[hsl(var(--muted))] border-b">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-[hsl(var(--accent))]" />
          <CardTitle className="text-xl font-[var(--font-display)]">
            45-Second Timer
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-8">
        {/* Circular Progress Ring */}
        <div className="relative mx-auto mb-6" style={{ width: 200, height: 200 }}>
          <svg className="transform -rotate-90" width="200" height="200">
            {/* Background circle */}
            <circle
              cx="100"
              cy="100"
              r="90"
              stroke="hsl(var(--muted))"
              strokeWidth="12"
              fill="none"
            />
            {/* Progress circle */}
            <circle
              cx="100"
              cy="100"
              r="90"
              stroke={hasEnded ? 'hsl(var(--destructive))' : isDanger ? 'hsl(var(--destructive))' : isWarning ? 'hsl(var(--secondary))' : 'hsl(var(--accent))'}
              strokeWidth="12"
              fill="none"
              strokeDasharray={565.48}
              strokeDashoffset={565.48 * (1 - progress / 100)}
              className="transition-all duration-1000 ease-linear"
              strokeLinecap="round"
            />
          </svg>
          {/* Time display */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div 
                className={`text-6xl font-[var(--font-display)] transition-colors ${
                  hasEnded ? 'text-[hsl(var(--destructive))]' :
                  isDanger ? 'text-[hsl(var(--destructive))] animate-pulse' :
                  isWarning ? 'text-[hsl(var(--secondary))]' :
                  'text-[hsl(var(--accent))]'
                }`}
                data-testid="timer-display"
              >
                {timeLeft}
              </div>
              <div className="text-sm text-muted-foreground font-[var(--font-mono)]">
                seconds
              </div>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-4">
          <Progress 
            value={progress} 
            className="h-2"
            data-testid="timer-progress"
          />
        </div>

        {/* Timer status */}
        {hasEnded && (
          <div className="mb-4 text-center">
            <Badge variant="destructive" className="text-sm">
              Time Expired!
            </Badge>
          </div>
        )}

        {/* Controls */}
        <div className="flex gap-2 justify-center">
          {!isRunning ? (
            <Button
              onClick={handleStart}
              className="flex-1 max-w-xs rounded-xl"
              size="lg"
              data-testid="timer-start-button"
            >
              <Play className="mr-2 h-5 w-5" />
              {timeLeft === TIMER_DURATION ? 'Start' : 'Resume'}
            </Button>
          ) : (
            <Button
              onClick={handlePause}
              variant="secondary"
              className="flex-1 max-w-xs rounded-xl"
              size="lg"
              data-testid="timer-pause-button"
            >
              <Pause className="mr-2 h-5 w-5" />
              Pause
            </Button>
          )}
          <Button
            onClick={handleReset}
            variant="outline"
            size="lg"
            className="rounded-xl"
            data-testid="timer-reset-button"
          >
            <RotateCcw className="h-5 w-5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default Timer;
